<template>
  <div>
    <Nav></Nav>
    <b-overlay :show="loadingReports" rounded="sm" spinner-type="grow">
      <b-container fluid>
        <b-row>
          <b-col>
            <h1>Tasa de Propina sobre Ventas</h1>
            <p class="text-muted">Porcentaje de propina respecto a ventas por mesero y sucursal</p>
          </b-col>
        </b-row>

        <ReportSearchBar @search="searchRecv" @changed="filtersChanged"></ReportSearchBar>

        <b-card v-if="filtersHaveChanged" class="mt-5">
          <b-row>
            <b-col class="text-center py-5">
              <b-icon icon="search" font-scale="3" variant="primary"></b-icon>
              <h4 class="mt-3">Filtros modificados</h4>
              <p class="text-muted">Presiona el botón "Buscar" para actualizar el reporte</p>
            </b-col>
          </b-row>
        </b-card>

        <b-card class="mt-5" v-else-if="reportTable.length > 0 && !filtersHaveChanged">
          <b-row class="mb-4">
            <b-col md="4" sm="6" class="mb-3">
              <b-card bg-variant="success" text-variant="white">
                <h4>${{ totalSales.toFixed(2) }}</h4>
                <small>Ventas Totales</small>
              </b-card>
            </b-col>
            <b-col md="4" sm="6" class="mb-3">
              <b-card bg-variant="warning" text-variant="white">
                <h4>${{ totalTips.toFixed(2) }}</h4>
                <small>Propinas Totales</small>
              </b-card>
            </b-col>
            <b-col md="4" sm="6" class="mb-3">
              <b-card bg-variant="primary" text-variant="white">
                <h4>{{ globalRate.toFixed(1) }}%</h4>
                <small>Tasa Global</small>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-button variant="primary" @click="exportToExcel">
                <b-icon icon="download"></b-icon> Descargar Excel
              </b-button>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col>
              <b-table
                small
                striped
                hover
                :items="reportTable"
                :fields="tableFields"
                responsive
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
              >
                <template #cell(total_sales)="data">${{ (data.value || 0).toFixed(2) }}</template>
                <template #cell(total_tips)="data">${{ (data.value || 0).toFixed(2) }}</template>
                <template #cell(tip_rate)="data">
                  <span :class="data.value < 5 ? 'text-danger font-weight-bold' : ''">
                    {{ (data.value || 0).toFixed(1) }}%
                  </span>
                </template>
              </b-table>
            </b-col>
          </b-row>
        </b-card>

        <b-card v-else-if="!loadingReports && !filtersHaveChanged" class="mt-5">
          <b-row>
            <b-col class="text-center py-5">
              <b-icon icon="info-circle" font-scale="3" variant="secondary"></b-icon>
              <h4 class="mt-3">No hay datos disponibles</h4>
              <p class="text-muted">Selecciona un rango de fechas y sucursales para ver el reporte</p>
            </b-col>
          </b-row>
        </b-card>
      </b-container>
    </b-overlay>
  </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import axios from "axios";
import moment from "moment";
import ReportSearchBar from "./searchBar/ReportSearchBar.vue";
import * as XLSX from "xlsx";

export default {
  name: "TipRate",
  components: { Nav, ReportSearchBar },
  data() {
    return {
      loadingReports: false,
      reportTable: [],
      filtersHaveChanged: false,
      dates: {
        initial_date: "",
        end_date: "",
        sucursales: [],
      },
      sortBy: "tip_rate",
      sortDesc: true,
      tableFields: [
        { key: "sucursal_name", label: "Sucursal", sortable: true },
        { key: "waiter_name", label: "Mesero", sortable: true, stickyColumn: true },
        { key: "total_sales", label: "Ventas", sortable: true },
        { key: "total_tips", label: "Propinas", sortable: true },
        { key: "tip_rate", label: "% Propina", sortable: true },
      ],
    };
  },
  computed: {
    totalSales() {
      return this.reportTable.reduce((s, i) => s + (i.total_sales || 0), 0);
    },
    totalTips() {
      return this.reportTable.reduce((s, i) => s + (i.total_tips || 0), 0);
    },
    globalRate() {
      return this.totalSales > 0 ? (this.totalTips / this.totalSales) * 100 : 0;
    },
  },
  methods: {
    filtersChanged() {
      this.filtersHaveChanged = true;
    },
    searchRecv(p) {
      this.dates.initial_date = moment(p.initDate).format("YYYY-MM-DD") + " 00:00:00";
      this.dates.end_date = moment(p.endDate).format("YYYY-MM-DD") + " 23:59:59";
      this.dates.sucursales = p.sucursales;
      this.filtersHaveChanged = false;
      this.getReport();
    },
    getReport() {
      this.loadingReports = true;
      let that = this;
      this.reportTable = [];
      axios
        .post("/report/tips/rate", this.dates)
        .then(function (response) {
          that.reportTable = response.data || [];
        })
        .catch(function (err) {
          that.makeToast("Error", err.response?.data || "Error al cargar el reporte", "danger");
        })
        .finally(() => {
          that.loadingReports = false;
        });
    },
    exportToExcel() {
      const workbook = XLSX.utils.book_new();
      const headers = this.tableFields.map((f) => f.label);
      const data = [headers];
      this.reportTable.forEach((item) => {
        data.push(this.tableFields.map((f) => (typeof item[f.key] === "number" ? item[f.key] : item[f.key] || "")));
      });
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = 1; R <= range.e.r; ++R) {
        this.tableFields.forEach((f, C) => {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!worksheet[addr]) return;
          if (["total_sales", "total_tips"].includes(f.key)) {
            worksheet[addr].z = "$#,##0.00";
          } else if (f.key === "tip_rate") {
            worksheet[addr].z = "0.0%";
            if (typeof worksheet[addr].v === "number") worksheet[addr].v = worksheet[addr].v / 100;
          }
        });
      }
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tasa de Propina");
      XLSX.writeFile(workbook, `tasa_propina_${this.dates.initial_date}_a_${this.dates.end_date}.xlsx`);
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, autoHideDelay: 5000, appendToast: true });
    },
  },
};
</script>

<style scoped>
.b-table th {
  white-space: nowrap;
}
</style>
